import { CreateInDialogButtonProps, FormDialogContext } from '@react-admin/ra-form-layout';
import { ReactElement } from 'react';
import { FileField, Labeled, required, SelectInput, useNotify, useRefresh } from 'react-admin';

import { DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE } from '@Common/constants/documents';
import {
  GetInstrumentOneResponse,
  InstrumentDocumentLanguageValues,
  InstrumentDocumentType,
  PostInstrumentDocumentRequest,
} from '@Common/types/bo/instruments';

import {
  ActionModalFormWithToolbar,
  CreateInDialogButton,
  DialogToolbar,
  FileInput,
  SaveButton,
} from '@sh4res/bo-common/components';
import { IdField } from '@sh4res/bo-common/fields';
import { useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { FileInputValue, getErrorMessage, makeChoicesCommon } from '@sh4res/bo-common/utils';

export type InstrumentDocumentsCreateValues = Pick<
  PostInstrumentDocumentRequest,
  'instrument_id' | 'type' | 'language'
> & {
  document_file?: FileInputValue;
};

const allInstrumentDocumentTypeChoices = makeChoicesCommon<InstrumentDocumentType>()({
  kid: 'resources.bo_common.instruments.documents.create.choices.kid',
  prospectus: 'resources.bo_common.instruments.documents.create.choices.prospectus',
  complexity_and_target_market_assessment:
    'resources.bo_common.instruments.documents.create.choices.complexity_and_target_market_assessment',
  brochure: 'resources.bo_common.instruments.documents.create.choices.brochure',
  term_sheet: 'resources.bo_common.instruments.documents.create.choices.term_sheet',
});

export const InstrumentDocumentsCreate = ({
  instrument,
  icon,
  ...props
}: CreateInDialogButtonProps['ButtonProps'] & {
  instrument: Pick<GetInstrumentOneResponse, 'id' | 'type'>;
  icon?: ReactElement;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { upload } = useUploadPresignedUrl();

  const availableDocumentTypes = [
    ...(['stock-us', 'stock-uv-eu'].includes(instrument.type)
      ? (['kid', 'complexity_and_target_market_assessment'] as const)
      : []),
    ...(['mutual-fund'].includes(instrument.type)
      ? ([
          'kid',
          'prospectus',
          'complexity_and_target_market_assessment',
          'brochure',
          'term_sheet',
        ] as const)
      : []),
  ] as InstrumentDocumentType[];

  const instrumentDocumentTypeChoices = allInstrumentDocumentTypeChoices.filter((choice) =>
    availableDocumentTypes.includes(choice.id),
  );

  const handleTransform = async (
    data: InstrumentDocumentsCreateValues,
  ): Promise<PostInstrumentDocumentRequest> => {
    if (!data.document_file || !(data.document_file.rawFile instanceof File)) {
      throw new Error('Error during upload');
    }
    const uploadResult = await upload(data.document_file.rawFile);

    if (!uploadResult) {
      throw new Error('Error during upload');
    }

    return {
      instrument_id: data.instrument_id,
      type: data.type,
      language: data.language,
      presigned_path: uploadResult.path,
    };
  };

  const onSuccess = () => {
    notify('resources.bo_common.instruments.documents.create.success', {
      type: 'success',
      messageArgs: { smart_count: 1 },
    });
    refresh();
  };

  const onError = (error: unknown) => {
    notify('resources.bo_common.instruments.documents.create.failure', {
      type: 'warning',
      messageArgs: { error: getErrorMessage(error) },
      multiLine: true,
    });
  };

  return (
    <CreateInDialogButton
      testId="BoExternalInstrumentDocumentsCreate_CreateButton"
      resource="instruments/documents"
      fullWidth
      maxWidth="sm"
      title="resources.bo_common.instruments.documents.create.title"
      ButtonProps={{
        variant: 'contained',
        color: 'info',
        size: 'small',
        sx: {
          fontWeight: 'bold',
          textTransform: 'capitalize',
        },
        ...props,
      }}
      icon={icon}
      record={{
        instrument_id: instrument.id,
      }}
      transform={handleTransform}
      mutationOptions={{ onSuccess, onError }}>
      <ActionModalFormWithToolbar
        toolbar={
          <DialogToolbar
            formDialogContext={FormDialogContext}
            button={
              <SaveButton
                color="primary"
                permissions={{
                  resource: 'instruments/documents',
                  action: 'create',
                }}
                testId="BoExternalInstrumentDocumentsCreate_SaveButton"
              />
            }
          />
        }>
        <Labeled label="resources.bo_common.instruments.fields.id">
          <IdField
            source="instrument_id"
            reference="instruments"
            data-testid="BoExternalInstrumentDocumentsCreate_InstrumentIdField"
          />
        </Labeled>
        <SelectInput
          source="type"
          validate={required()}
          choices={instrumentDocumentTypeChoices}
          data-testid="BoExternalInstrumentDocumentsCreate_TypeInput"
        />
        <SelectInput
          source="language"
          validate={required()}
          choices={InstrumentDocumentLanguageValues.map((language) => ({
            id: language,
            name: language,
          }))}
          data-testid="BoExternalInstrumentDocumentsCreate_LanguageInput"
        />
        <FileInput
          source="document_file"
          accept="application/pdf"
          maxSize={DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE}
          validate={required()}
          data-testid="BoExternalInstrumentDocumentsCreate_DocumentFileInput">
          <FileField
            source="src"
            title="title"
            data-testid="BoExternalInstrumentDocumentsCreate_DocumentFileField"
          />
        </FileInput>
      </ActionModalFormWithToolbar>
    </CreateInDialogButton>
  );
};
